import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import { EXT } from "../data";
import { useLang } from "../i18n";

const SLIDES = [
  "/images/f8qbkm4e_amliore_la_qualit_de_limage_et_Nano_Banana_2_38106.webp",
  EXT.nightVilla,
  "/images/n6h625we_8.webp",
  "/images/kxoyqnm1_3.webp",
  "/images/q3bzxz17_4.webp",
  "/images/s2ff3j38_25.webp",
];

const line = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 1.05, delay: 0.35 + i * 0.13, ease: [0.19, 1, 0.22, 1] },
  }),
};

export const Hero = () => {
  const { t } = useLang();
  const ref = useRef(null);
  const [slide, setSlide] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => setSlide((s) => (s + 1) % SLIDES.length), 4000);
    return () => clearInterval(timer);
  }, [slide]);

  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "24%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const overlay = useTransform(scrollYProgress, [0, 1], [0.35, 0.75]);

  return (
    <section id="hero" ref={ref} className="relative h-[100svh] w-full overflow-hidden" data-testid="hero-section">
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <AnimatePresence initial={false}>
          <motion.img
            key={slide}
            src={SLIDES[slide]}
            alt="Villa Tutto Passa à Cala Rossa"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.9, ease: "easeInOut" }}
            className="absolute inset-0 h-full w-full object-cover object-center"
            data-testid="hero-slide"
          />
        </AnimatePresence>
      </motion.div>
      <motion.div
        style={{ opacity: overlay }}
        className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-black/30"
      />

      <div className="absolute inset-0 z-10 flex flex-col justify-end px-6 md:px-12 pb-16 md:pb-20 max-w-[1600px] mx-auto pointer-events-none">
        <h1 className="font-serif font-light text-white leading-[1.02] text-[9vw] md:text-[5vw] tracking-tight max-w-5xl">
          {t.hero.titleLines.map((word, i) => (
            <span key={word} className="reveal-line">
              <motion.span custom={i} variants={line} initial="hidden" animate="show" className="block italic">
                {word}
              </motion.span>
            </span>
          ))}
        </h1>

        <div className="mt-8" />
      </div>

      <motion.button
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8, duration: 1 }}
        onClick={() => setSlide((s) => (s + 1) % SLIDES.length)}
        data-testid="hero-scroll-btn"
        aria-label="Photo suivante"
        className="absolute bottom-6 right-6 md:right-12 z-10 text-white/70 hover:text-white transition-colors text-[11px] tracking-[0.3em] uppercase writing-mode-vertical hidden md:block cursor-pointer"
      >
        {t.hero.scroll}
      </motion.button>

      <div className="absolute bottom-6 left-6 md:left-12 z-10 flex gap-2" data-testid="hero-dots">
        {SLIDES.map((_, i) => (
          <button
            key={i}
            onClick={() => setSlide(i)}
            aria-label={`Photo ${i + 1}`}
            data-testid={`hero-dot-${i}`}
            className={`h-[3px] transition-all duration-500 ${i === slide ? "w-8 bg-white" : "w-4 bg-white/40 hover:bg-white/70"}`}
          />
        ))}
      </div>
    </section>
  );
};
